import { useState } from 'react';
import {
    Card,
    Text,
    Group,
    Stack,
    Title,
    ActionIcon,
    Button,
    Badge,
    Progress,
    TextInput,
    NumberInput,
    Modal,
    Accordion,
    Box,
    useMantineTheme,
    Select,
} from '@mantine/core';
import { BudgetSummary, BudgetCategory, BudgetHeader } from '../types/budget';
import { notifications } from '@mantine/notifications';
import { env } from '../utils/env';
import editIcon from '../assets/icons/edit.svg';
import checkIcon from '../assets/icons/check.svg';
import closeIcon from '../assets/icons/close.svg';
import plusIcon from '../assets/icons/plus.svg';
import trashIcon from '../assets/icons/trash.svg';
import * as Sentry from '@sentry/react';

interface BudgetCategoriesProps {
    budgetSummary: BudgetSummary;
    accessToken: string;
    onUpdate: () => void;
}

export default function BudgetCategories({
    budgetSummary,
    accessToken,
    onUpdate,
}: BudgetCategoriesProps) {
    const theme = useMantineTheme();
    const [editingCategory, setEditingCategory] = useState<number | null>(null);
    const [editAmount, setEditAmount] = useState<number | ''>(0);
    const [editFrequencyType, setEditFrequencyType] = useState<string>('month');
    const [editFrequencyValue, setEditFrequencyValue] = useState<number | ''>(1);
    const [editCategoryName, setEditCategoryName] = useState('');

    // New state for new category modal
    const [newCategoryModal, setNewCategoryModal] = useState(false);
    const [selectedHeaderId, setSelectedHeaderId] = useState<number | null>(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryAmount, setNewCategoryAmount] = useState<number | ''>(0);
    const [newCategoryFrequency, setNewCategoryFrequency] = useState<number | ''>(1);
    const [newCategoryFrequencyType, setNewCategoryFrequencyType] = useState('month');

    // New state for new header modal
    const [newHeaderModal, setNewHeaderModal] = useState(false);
    const [newHeaderName, setNewHeaderName] = useState('');
    const [newHeaderType, setNewHeaderType] = useState('discretionary');

    // Function to get color for header
    const getHeaderColor = (headerName: string): string => {
        if (headerName.toLowerCase().includes('essential') ||
            headerName.toLowerCase().includes('needs')) return 'blue';
        if (headerName.toLowerCase().includes('savings')) return 'teal';
        if (headerName.toLowerCase().includes('want')) return 'grape';
        if (headerName.toLowerCase().includes('discretionary')) return 'indigo';
        return 'gray';
    };

    // Function to get header opacity based on its budget percentage
    const getHeaderOpacity = (header: BudgetHeader): number => {
        const totalBudget = budgetSummary.monthly_total || 1; // Avoid division by zero
        const headerPercentage = (header.monthly_total / totalBudget);
        return 0.6 + (headerPercentage * 0.4); // Range from 0.6 to 1.0
    };

    // Calculate header spending progress percentage
    const calculateHeaderProgress = (header: BudgetHeader): number => {
        if (header.monthly_total === 0) return 0;

        const totalSpent = header.categories.reduce((sum, category) => sum + category.spent, 0);
        return (totalSpent / header.monthly_total) * 100;
    };

    // Calculate total spent for a header
    const calculateHeaderSpent = (header: BudgetHeader): number => {
        return header.categories.reduce((sum, category) => sum + category.spent, 0);
    };

    // Function to handle category edit
    const handleCategoryEdit = async (categoryId: number) => {
        try {
            console.log(`Updating category ${categoryId} with data:`, {
                name: editCategoryName,
                allocated: editAmount,
                frequency_type: editFrequencyType,
                frequency_value: editFrequencyValue
            });

            const url = `${env.API_URL}/api/budget/categories/${categoryId}`;
            console.log(`Making PATCH request to: ${url}`);

            const response = await fetch(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    name: editCategoryName,
                    allocated: editAmount,
                    frequency_type: editFrequencyType,
                    frequency_value: editFrequencyValue,
                }),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Server returned ${response.status}: ${errorText}`);
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch (e) {
                    errorData = { detail: errorText || 'Failed to update category' };
                }
                throw new Error(errorData.detail || 'Failed to update category');
            }

            notifications.show({
                title: 'Category Updated',
                message: `Successfully updated ${editCategoryName}`,
                color: 'green',
            });

            // Reset edit state and refresh
            setEditingCategory(null);
            onUpdate();
        } catch (error) {
            console.error('Error updating category:', error);
            Sentry.captureException(error);
            notifications.show({
                title: 'Update Failed',
                message: error instanceof Error ? error.message : 'Failed to update category. Please try again.',
                color: 'red',
            });
        }
    };

    // Function to create new category
    const handleCreateCategory = async () => {
        if (!selectedHeaderId || !newCategoryName.trim()) {
            notifications.show({
                title: 'Invalid Data',
                message: 'Please provide a valid category name and header',
                color: 'red',
            });
            return;
        }

        try {
            const response = await fetch(`${env.API_URL}/api/budget/categories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    header_id: selectedHeaderId,
                    name: newCategoryName.trim(),
                    allocated: newCategoryAmount || 0,
                    frequency_type: newCategoryFrequencyType,
                    frequency_value: newCategoryFrequency || 1,
                }),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to create category');
            }

            notifications.show({
                title: 'Category Created',
                message: `Successfully created ${newCategoryName}`,
                color: 'green',
            });

            // Reset form and close modal
            setNewCategoryModal(false);
            setNewCategoryName('');
            setNewCategoryAmount(0);
            setNewCategoryFrequency(1);
            setSelectedHeaderId(null);
            onUpdate();
        } catch (error) {
            console.error('Error creating category:', error);
            Sentry.captureException(error);
            notifications.show({
                title: 'Creation Failed',
                message: error instanceof Error ? error.message : 'Failed to create category. Please try again.',
                color: 'red',
            });
        }
    };

    // Function to create new header
    const handleCreateHeader = async () => {
        if (!newHeaderName.trim()) {
            notifications.show({
                title: 'Invalid Data',
                message: 'Please provide a valid header name',
                color: 'red',
            });
            return;
        }

        try {
            const response = await fetch(`${env.API_URL}/api/budget/headers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    plan_id: budgetSummary.id,
                    name: newHeaderName.trim(),
                    spending_type: newHeaderType,
                }),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to create header');
            }

            notifications.show({
                title: 'Header Created',
                message: `Successfully created ${newHeaderName}`,
                color: 'green',
            });

            // Reset form and close modal
            setNewHeaderModal(false);
            setNewHeaderName('');
            setNewHeaderType('discretionary');
            onUpdate();
        } catch (error) {
            console.error('Error creating header:', error);
            Sentry.captureException(error);
            notifications.show({
                title: 'Creation Failed',
                message: error instanceof Error ? error.message : 'Failed to create header. Please try again.',
                color: 'red',
            });
        }
    };

    // Function to delete category
    const handleDeleteCategory = async (categoryId: number, categoryName: string) => {
        if (!confirm(`Are you sure you want to delete "${categoryName}"?`)) {
            return;
        }

        try {
            const response = await fetch(`${env.API_URL}/api/budget/categories/${categoryId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to delete category');
            }

            notifications.show({
                title: 'Category Deleted',
                message: `Successfully removed ${categoryName}`,
                color: 'green',
            });

            onUpdate();
        } catch (error) {
            console.error('Error deleting category:', error);
            Sentry.captureException(error);
            notifications.show({
                title: 'Deletion Failed',
                message: error instanceof Error ? error.message : 'Failed to delete category. Please try again.',
                color: 'red',
            });
        }
    };

    // Function to delete header
    const handleDeleteHeader = async (headerId: number, headerName: string) => {
        if (!confirm(`Are you sure you want to delete the group "${headerName}" and all its categories?`)) {
            return;
        }

        try {
            const response = await fetch(`${env.API_URL}/api/budget/headers/${headerId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to delete budget group');
            }

            notifications.show({
                title: 'Group Deleted',
                message: `Successfully removed ${headerName}`,
                color: 'green',
            });

            onUpdate();
        } catch (error) {
            console.error('Error deleting header:', error);
            Sentry.captureException(error);
            notifications.show({
                title: 'Deletion Failed',
                message: error instanceof Error ? error.message : 'Failed to delete group. Please try again.',
                color: 'red',
            });
        }
    };

    // Helper to start editing a category
    const startEditingCategory = (category: BudgetCategory) => {
        setEditingCategory(category.id);
        setEditAmount(category.allocated);
        setEditFrequencyType(category.frequency_type);
        setEditFrequencyValue(category.frequency_value);
        setEditCategoryName(category.name);
    };

    // Helper to cancel editing
    const cancelEditing = () => {
        setEditingCategory(null);
    };

    return (
        <Stack gap="lg" p="md">
            {/* Header with buttons for new items */}
            <Group justify="space-between" align="center">
                <Title order={4}>Budget Categories</Title>
                <Group>
                    <Button
                        variant="outline"
                        size="sm"
                        leftSection={<img src={plusIcon} alt="Add" width={14} height={14} />}
                        onClick={() => setNewHeaderModal(true)}
                    >
                        New Group
                    </Button>
                    <Button
                        variant="filled"
                        size="sm"
                        leftSection={<img src={plusIcon} alt="Add" width={14} height={14} />}
                        onClick={() => setNewCategoryModal(true)}
                    >
                        New Category
                    </Button>
                </Group>
            </Group>

            {/* Headers and Categories */}
            <Accordion variant="filled" radius="md">
                {budgetSummary.headers
                    .filter(header => header.name !== 'Uncategorized' || (header.name === 'Uncategorized' && header.categories.length > 0))
                    .map(header => (
                        <Accordion.Item
                            key={header.id}
                            value={header.id.toString()}
                        >
                            <Accordion.Control>
                                <Group justify="space-between" wrap="nowrap">
                                    <Box style={{ flex: 1 }}>
                                        <Group gap="xs">
                                            <Text fw={600}>{header.name}</Text>
                                            <Badge
                                                color={getHeaderColor(header.name)}
                                                variant="light"
                                            >
                                                {header.categories.length} categories
                                            </Badge>
                                        </Group>

                                        {/* Add header progress bar */}
                                        {calculateHeaderProgress(header) > 0 && (
                                            <>
                                                <Progress
                                                    value={Math.min(calculateHeaderProgress(header), 100)}
                                                    color={calculateHeaderProgress(header) > 90 ? 'red' :
                                                        calculateHeaderProgress(header) > 75 ? 'orange' :
                                                            calculateHeaderProgress(header) > 50 ? 'blue' : 'teal'}
                                                    size="sm"
                                                    radius="xl"
                                                    mt={8}
                                                    mb={2}
                                                    style={{
                                                        transition: 'width 0.3s ease',
                                                    }}
                                                />
                                                <Group justify="space-between" gap="xs">
                                                    <Text size="xs" c="dimmed">
                                                        ${calculateHeaderSpent(header).toFixed(2)} spent
                                                    </Text>
                                                    <Group gap={4}>
                                                        <Text
                                                            size="xs"
                                                            fw={500}
                                                            c={calculateHeaderProgress(header) > 90 ? 'red' :
                                                                calculateHeaderProgress(header) > 75 ? 'orange' : 'dimmed'}
                                                        >
                                                            {calculateHeaderProgress(header).toFixed(0)}%
                                                        </Text>
                                                        <Text size="xs" c="dimmed">of ${header.monthly_total.toFixed(2)}</Text>
                                                    </Group>
                                                </Group>
                                            </>
                                        )}
                                    </Box>
                                    <Group gap="xs" wrap="nowrap">
                                        <Text fw={500} c="dimmed">${header.monthly_total.toFixed(2)}/mo</Text>
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteHeader(header.id, header.name);
                                            }}
                                            aria-label="Delete budget group"
                                        >
                                            <img src={trashIcon} alt="Delete" width={16} height={16} />
                                        </ActionIcon>
                                    </Group>
                                </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap="xs">
                                    {header.categories.map(category => (
                                        <Card
                                            key={category.id}
                                            p="sm"
                                            withBorder
                                            radius="md"
                                            style={{
                                                borderLeftWidth: '4px',
                                                borderLeftStyle: 'solid',
                                                borderLeftColor: theme.colors[getHeaderColor(header.name)][6]
                                            }}
                                        >
                                            {editingCategory === category.id ? (
                                                <Stack gap="xs">
                                                    <TextInput
                                                        placeholder="Category name"
                                                        value={editCategoryName}
                                                        onChange={(e) => setEditCategoryName(e.target.value)}
                                                        required
                                                    />
                                                    <Group grow>
                                                        <NumberInput
                                                            label="Amount"
                                                            value={editAmount}
                                                            onChange={(value) => setEditAmount(value as number | '')}
                                                            min={0}
                                                            decimalScale={2}
                                                            fixedDecimalScale
                                                            prefix="$"
                                                        />
                                                        <Select
                                                            label="Recurrence"
                                                            value={editFrequencyType}
                                                            onChange={(value) => value && setEditFrequencyType(value)}
                                                            data={[
                                                                { value: 'month', label: 'Monthly' },
                                                                { value: 'week', label: 'Weekly' }
                                                            ]}
                                                        />
                                                        <NumberInput
                                                            label={editFrequencyType === 'month' ? 'Every __ Months' : 'Every __ Weeks'}
                                                            value={editFrequencyValue}
                                                            onChange={(value) => setEditFrequencyValue(value as number | '')}
                                                            min={1}
                                                            max={editFrequencyType === 'month' ? 24 : 52}
                                                            description={editFrequencyValue === 1 ?
                                                                (editFrequencyType === 'month' ? 'Monthly expense' : 'Weekly expense') :
                                                                `Repeats every ${editFrequencyValue} ${editFrequencyType}${typeof editFrequencyValue === 'number' && editFrequencyValue > 1 ? 's' : ''}`}
                                                        />
                                                    </Group>
                                                    <Group justify="flex-end">
                                                        <Button
                                                            variant="subtle"
                                                            color="gray"
                                                            size="xs"
                                                            leftSection={<img src={closeIcon} alt="Cancel" width={14} height={14} />}
                                                            onClick={cancelEditing}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant="filled"
                                                            size="xs"
                                                            leftSection={<img src={checkIcon} alt="Save" width={14} height={14} />}
                                                            onClick={() => handleCategoryEdit(category.id)}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Group>
                                                </Stack>
                                            ) : (
                                                <Group justify="space-between" align="flex-start" wrap="nowrap">
                                                    <Stack gap={2} style={{ flex: 1 }}>
                                                        <Group justify="space-between" wrap="nowrap">
                                                            <Text fw={500}>{category.name}</Text>
                                                            <Group gap="xs">
                                                                <ActionIcon
                                                                    variant="subtle"
                                                                    color="gray"
                                                                    onClick={() => startEditingCategory(category)}
                                                                    aria-label="Edit category"
                                                                >
                                                                    <img src={editIcon} alt="Edit" width={16} height={16} />
                                                                </ActionIcon>
                                                                <ActionIcon
                                                                    variant="subtle"
                                                                    color="red"
                                                                    onClick={() => handleDeleteCategory(category.id, category.name)}
                                                                    aria-label="Delete category"
                                                                >
                                                                    <img src={trashIcon} alt="Delete" width={16} height={16} />
                                                                </ActionIcon>
                                                            </Group>
                                                        </Group>

                                                        <Group justify="space-between" gap="xs" wrap="nowrap">
                                                            <Text size="sm" c="dimmed">{category.frequency}</Text>
                                                            <Text size="sm" fw={500}>${category.allocated}</Text>
                                                        </Group>

                                                        <Group justify="space-between" gap="xs" wrap="nowrap">
                                                            <Text size="xs" c="dimmed">
                                                                ${category.monthly_amount.toFixed(2)}/mo · ${category.yearly_amount.toFixed(2)}/yr
                                                            </Text>
                                                        </Group>

                                                        {category.spent > 0 && (
                                                            <>
                                                                <Progress
                                                                    value={Math.min(category.percentage_used, 100)}
                                                                    color={category.percentage_used > 90 ? 'red' :
                                                                        category.percentage_used > 75 ? 'orange' :
                                                                            category.percentage_used > 50 ? 'blue' : 'teal'}
                                                                    size="sm"
                                                                    radius="xl"
                                                                    mt={4}
                                                                    style={{
                                                                        transition: 'width 0.3s ease',
                                                                    }}
                                                                />
                                                                <Group justify="space-between" wrap="nowrap">
                                                                    <Text size="xs" c="dimmed">
                                                                        ${category.spent.toFixed(2)} of ${category.allocated.toFixed(2)}
                                                                    </Text>
                                                                    <Text
                                                                        size="xs"
                                                                        fw={500}
                                                                        c={category.percentage_used > 90 ? 'red' :
                                                                            category.percentage_used > 75 ? 'orange' : 'dimmed'}
                                                                    >
                                                                        {category.percentage_used.toFixed(0)}%
                                                                    </Text>
                                                                </Group>
                                                            </>
                                                        )}
                                                    </Stack>
                                                </Group>
                                            )}
                                        </Card>
                                    ))}
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))
                }
            </Accordion>

            {/* New Category Modal */}
            <Modal
                opened={newCategoryModal}
                onClose={() => setNewCategoryModal(false)}
                title="Create New Category"
                centered
            >
                <Stack gap="md">
                    <Select
                        label="Budget Group"
                        placeholder="Select a group"
                        data={budgetSummary.headers.map(h => ({ value: h.id.toString(), label: h.name }))}
                        value={selectedHeaderId?.toString() || ''}
                        onChange={(value) => setSelectedHeaderId(value ? parseInt(value) : null)}
                        required
                    />
                    <TextInput
                        label="Category Name"
                        placeholder="e.g., Rent, Groceries"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        required
                    />
                    <NumberInput
                        label="Allocated Amount"
                        value={newCategoryAmount}
                        onChange={(value) => setNewCategoryAmount(value as number | '')}
                        min={0}
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="$"
                    />
                    <Group grow>
                        <Select
                            label="Recurrence Type"
                            value={newCategoryFrequencyType}
                            onChange={(value) => value && setNewCategoryFrequencyType(value)}
                            data={[
                                { value: 'month', label: 'Monthly' },
                                { value: 'week', label: 'Weekly' }
                            ]}
                            description="How often this expense occurs"
                        />
                        <NumberInput
                            label={newCategoryFrequencyType === 'month' ? 'Every __ Months' : 'Every __ Weeks'}
                            value={newCategoryFrequency}
                            onChange={(value) => setNewCategoryFrequency(value as number | '')}
                            min={1}
                            max={newCategoryFrequencyType === 'month' ? 24 : 52}
                            description={newCategoryFrequency === 1 ?
                                (newCategoryFrequencyType === 'month' ? 'Monthly expense' : 'Weekly expense') :
                                `Repeats every ${newCategoryFrequency} ${newCategoryFrequencyType}${typeof newCategoryFrequency === 'number' && newCategoryFrequency > 1 ? 's' : ''}`}
                        />
                    </Group>
                    <Group justify="flex-end" mt="md">
                        <Button variant="subtle" onClick={() => setNewCategoryModal(false)}>Cancel</Button>
                        <Button onClick={handleCreateCategory}>Create Category</Button>
                    </Group>
                </Stack>
            </Modal>

            {/* New Header Modal */}
            <Modal
                opened={newHeaderModal}
                onClose={() => setNewHeaderModal(false)}
                title="Create New Budget Group"
                centered
            >
                <Stack gap="md">
                    <TextInput
                        label="Group Name"
                        placeholder="e.g., Essential Expenses, Fun Money"
                        value={newHeaderName}
                        onChange={(e) => setNewHeaderName(e.target.value)}
                        required
                    />
                    <Select
                        label="Spending Type"
                        value={newHeaderType}
                        onChange={(value) => value && setNewHeaderType(value)}
                        data={[
                            { value: 'essential', label: 'Essential' },
                            { value: 'discretionary', label: 'Discretionary' },
                            { value: 'uncategorized', label: 'Uncategorized' }
                        ]}
                    />
                    <Group justify="flex-end" mt="md">
                        <Button variant="subtle" onClick={() => setNewHeaderModal(false)}>Cancel</Button>
                        <Button onClick={handleCreateHeader}>Create Group</Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
} 